import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './button';
import { Card, CardContent } from './card';
import { X, Camera, ScanLine, Loader2 } from 'lucide-react';

const BarcodeScanner = ({ onScan, onClose, isOpen }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const html5QrCodeRef = useRef(null);
  const mountedRef = useRef(true);
  const scannerContainerId = 'qr-scanner-container';

  // Cleanup function
  const cleanupScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        // State 2 = SCANNING
        if (state === 2) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.log("Scanner cleanup:", err.message);
      } finally {
        html5QrCodeRef.current = null;
      }
    }
    if (mountedRef.current) {
      setIsScanning(false);
      setIsStarting(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupScanner();
    };
  }, [cleanupScanner]);

  useEffect(() => {
    if (isOpen && !isScanning && !isStarting && !error) {
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          startScanner();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    
    if (!isOpen) {
      cleanupScanner();
    }
  }, [isOpen]);

  const startScanner = async () => {
    if (isStarting || isScanning) return;
    
    setIsStarting(true);
    setError(null);
    
    try {
      // Wait for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const element = document.getElementById(scannerContainerId);
      if (!element) {
        throw new Error("Scanner container not found");
      }

      // Clear any existing content
      element.innerHTML = '';

      const html5QrCode = new Html5Qrcode(scannerContainerId, {
        verbose: false,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      });
      html5QrCodeRef.current = html5QrCode;

      // iOS Safari needs specific configuration
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: isIOS ? 1.777778 : 1.333333, // 16:9 for iOS, 4:3 for others
        disableFlip: false,
        formatsToSupport: [0] // Only QR_CODE for better performance
      };

      // Use different camera config for iOS
      const cameraConfig = isIOS 
        ? { facingMode: { exact: "environment" } }
        : { facingMode: "environment" };

      await html5QrCode.start(
        cameraConfig,
        config,
        (decodedText) => {
          if (mountedRef.current) {
            handleScan(decodedText);
          }
        },
        () => {} // Ignore scan failures
      );
      
      // Fix iOS black screen - force video element styles
      setTimeout(() => {
        const video = element.querySelector('video');
        if (video) {
          video.setAttribute('playsinline', 'true');
          video.setAttribute('webkit-playsinline', 'true');
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'cover';
          video.style.transform = 'none';
        }
      }, 500);
      
      if (mountedRef.current) {
        setIsScanning(true);
        setIsStarting(false);
      }
    } catch (err) {
      console.error("Scanner error:", err);
      let errorMessage = "Could not start camera.";
      
      const errStr = err.toString().toLowerCase();
      if (errStr.includes("permission") || errStr.includes("notallowed")) {
        errorMessage = "Camera permission denied. Please allow camera access and try again.";
      } else if (errStr.includes("notfound") || errStr.includes("no camera")) {
        errorMessage = "No camera found on this device.";
      } else if (errStr.includes("notreadable") || errStr.includes("in use") || errStr.includes("could not start")) {
        errorMessage = "Camera may be in use by another app. Close other apps and try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (mountedRef.current) {
        setError(errorMessage);
        setIsStarting(false);
        setIsScanning(false);
      }
    }
  };

  const handleScan = (code) => {
    // Play beep sound
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 1000;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      oscillator.start();
      setTimeout(() => oscillator.stop(), 100);
    } catch (e) {
      // Ignore audio errors
    }

    cleanupScanner();
    onScan(code);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
    }
  };

  const handleClose = () => {
    cleanupScanner();
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    setIsStarting(false);
    setIsScanning(false);
    setTimeout(() => startScanner(), 200);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50">
        <div className="flex items-center gap-2 text-white">
          <ScanLine className="w-5 h-5" />
          <h3 className="font-semibold">Scan QR Code</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={handleClose} className="text-white hover:bg-white/20">
          <X className="w-6 h-6" />
        </Button>
      </div>

      {/* Scanner Area - Full screen for iOS */}
      <div className="flex-1 relative">
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 p-6">
            <Camera className="w-16 h-16 text-slate-500 mb-4" />
            <p className="text-white text-center mb-6">{error}</p>
            <Button 
              onClick={handleRetry}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6"
            >
              <Camera className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : (
          <>
            {/* Scanner container */}
            <div 
              id={scannerContainerId}
              className="absolute inset-0"
              style={{ 
                width: '100%', 
                height: '100%',
                overflow: 'hidden'
              }}
            />
            
            {/* Scanning overlay with frame */}
            {isScanning && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Semi-transparent overlay */}
                <div className="absolute inset-0 bg-black/40" />
                
                {/* Clear scanning area in center */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64">
                  <div className="absolute inset-0 bg-transparent" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }} />
                  {/* Corner markers */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-teal-400 rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-teal-400 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-teal-400 rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-teal-400 rounded-br-lg" />
                  {/* Scanning line animation */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-teal-400 animate-pulse" 
                       style={{ animation: 'scan 2s ease-in-out infinite' }} />
                </div>
                
                {/* Status text */}
                <div className="absolute bottom-32 left-0 right-0 text-center">
                  <div className="inline-flex items-center gap-2 bg-black/60 px-4 py-2 rounded-full">
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
                    <span className="text-white text-sm font-medium">Point at QR code</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Loading state */}
            {isStarting && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900">
                <Loader2 className="w-10 h-10 text-teal-400 animate-spin mb-4" />
                <p className="text-white">Starting camera...</p>
                <p className="text-slate-400 text-sm mt-2">Allow camera access if prompted</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Manual Entry - Bottom */}
      <div className="bg-white p-4 rounded-t-3xl">
        <p className="text-sm text-slate-500 mb-2 text-center">Or enter code manually:</p>
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <input
            type="text"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Enter SKU..."
            className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="manual-barcode-input"
          />
          <Button 
            type="submit" 
            className="bg-slate-900 hover:bg-slate-800 text-white px-6 rounded-xl"
            data-testid="manual-barcode-submit"
          >
            Search
          </Button>
        </form>
      </div>

      {/* CSS for scanning animation */}
      <style>{`
        @keyframes scan {
          0%, 100% { top: 0; }
          50% { top: calc(100% - 2px); }
        }
        #${scannerContainerId} video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
      `}</style>
    </div>
  );
};

export default BarcodeScanner;
